import { useEffect, useMemo, useRef, useState } from 'react'
import { Boxes, Camera, Pencil, Plus, Search, Trash2, X } from 'lucide-react'
import {
  eliminarInsumo,
  guardarInsumo,
  moverStock,
  traerInsumos,
  UNIDADES_INSUMO,
  type Insumo,
} from '../data/insumos'
import { supabaseConfigurado } from '../data/pos'
import { useSesion, usuarioActual } from '../store/sesion'
import { useUI } from '../store/ui'
import { Boton, Campo, Cargando, Entrada, Lista, Tarjeta, Vacio } from './ui/base'
import { Hoja } from './ui/Hoja'
import { cn, uid } from '../lib/cn'
import { fotoApp } from '../lib/fotos'
import { comprimirImagen } from '../lib/imagen'
import { money, normalizar, num } from '../lib/format'

/** Los insumos de la bodega: servilletas, vasos, bombillas.
 *
 *  Se piden a la base cuando se abre la pestaña, no al arrancar la app:
 *  solo los mira el administrador y no tiene sentido que cada vendedor
 *  los descargue al entrar.
 */
export function PanelInsumos() {
  const esAdmin = useSesion((s) => s.usuario?.rol === 'admin')
  const avisar = useUI((s) => s.avisar)

  const [insumos, setInsumos] = useState<Insumo[]>([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [texto, setTexto] = useState('')

  const [editando, setEditando] = useState<Insumo | 'nuevo' | null>(null)
  const [ajustando, setAjustando] = useState<Insumo | null>(null)

  const cargar = async () => {
    setCargando(true)
    setError(null)
    try {
      setInsumos(await traerInsumos())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron cargar los insumos.')
    } finally {
      setCargando(false)
    }
  }

  useEffect(() => {
    void cargar()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const lista = useMemo(() => {
    const q = normalizar(texto.trim())
    const filtrados = q
      ? insumos.filter((i) => [i.nombre, i.codigo, i.proveedor].some((c) => c && normalizar(c).includes(q)))
      : insumos
    // Los dados de baja al final; el resto por nombre.
    return [...filtrados].sort((a, b) =>
      a.activo !== b.activo ? (a.activo ? -1 : 1) : a.nombre.localeCompare(b.nombre, 'es'),
    )
  }, [insumos, texto])

  const bajos = lista.filter((i) => i.activo && i.stock <= i.stockMinimo).length
  const valorTotal = insumos.reduce((s, i) => s + i.stock * i.costoUnitario, 0)

  // Esta pantalla escribe en Supabase directo; sin él no hay nada que
  // mostrar y conviene decirlo en vez de dejar una lista vacía.
  if (!supabaseConfigurado) {
    return (
      <Vacio
        icono={<Boxes className="size-7" />}
        titulo="Los insumos necesitan Supabase"
        detalle="Esta pantalla lee y escribe directo en la base de datos compartida. Configúrala en el archivo .env para usarla."
      />
    )
  }

  if (cargando) return <Cargando texto="Cargando insumos…" />

  if (error) {
    return (
      <Vacio
        icono={<Boxes className="size-7" />}
        titulo="No se pudieron cargar los insumos"
        detalle={error}
        accion={
          <Boton variante="secundario" onClick={() => void cargar()}>
            Reintentar
          </Boton>
        }
      />
    )
  }

  return (
    <>
      {insumos.length > 0 && (
        <div className="mb-4 grid grid-cols-2 gap-2.5">
          <Tarjeta className="p-3.5">
            <p className="mb-1.5 text-[10px] leading-tight font-bold texto-suave uppercase">
              En bodega
            </p>
            <p className="cifra font-display text-2xl font-bold texto-base">{insumos.length}</p>
          </Tarjeta>
          <Tarjeta className="p-3.5">
            <p className="mb-1.5 text-[10px] leading-tight font-bold texto-suave uppercase">
              {bajos > 0 ? 'Hay que reponer' : 'Valor del stock'}
            </p>
            <p
              className={cn(
                'cifra font-display text-2xl font-bold',
                bajos > 0 ? 'text-ambar-600 dark:text-ambar-300' : 'texto-base',
              )}
            >
              {bajos > 0 ? bajos : money(valorTotal)}
            </p>
          </Tarjeta>
        </div>
      )}

      {insumos.length > 0 && (
        <div className="relative mb-4">
          <Search className="pointer-events-none absolute top-1/2 left-4 size-[18px] -translate-y-1/2 texto-tenue" />
          <input
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Buscar insumo…"
            className="h-12 w-full rounded-2xl border borde-base superficie-elevada pr-11 pl-11 text-[15px] texto-base transition-colors placeholder:texto-tenue focus:border-bosque-400 focus:outline-none focus:ring-4 focus:ring-bosque-500/10"
          />
          {texto && (
            <button
              onClick={() => setTexto('')}
              className="absolute top-1/2 right-3 grid size-8 -translate-y-1/2 place-items-center rounded-lg texto-tenue hover:texto-base"
              aria-label="Borrar búsqueda"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
      )}

      {lista.length === 0 ? (
        <Vacio
          icono={<Boxes className="size-7" />}
          titulo={texto ? 'Ningún insumo coincide' : 'Todavía no hay insumos'}
          detalle={
            texto
              ? 'Prueba con otro término.'
              : 'Servilletas, vasos, bombillas: lo que sale de la bodega pero no se vende.'
          }
          accion={
            esAdmin && !texto ? (
              <Boton onClick={() => setEditando('nuevo')} icono={<Plus className="size-[18px]" />}>
                Nuevo insumo
              </Boton>
            ) : undefined
          }
        />
      ) : (
        <div className="flex flex-col gap-2.5 escalonar">
          {lista.map((i) => {
            const bajo = i.activo && i.stock <= i.stockMinimo
            return (
              <Tarjeta key={i.id} className={cn('p-4', !i.activo && 'opacity-55')}>
                <div className="flex items-start gap-3">
                  {i.foto ? (
                    <img
                      src={fotoApp(i.foto)}
                      alt=""
                      className="size-12 shrink-0 rounded-xl object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <div className="grid size-12 shrink-0 place-items-center rounded-xl superficie-hundida texto-tenue">
                      <Boxes className="size-5" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[15px] font-bold texto-base">
                      {i.nombre}
                      {!i.activo && <span className="ml-2 text-[12px] texto-tenue">dado de baja</span>}
                    </p>
                    <p className="mt-0.5 truncate text-[13px] texto-suave">
                      <span className="codigo">{i.codigo}</span>
                      {i.proveedor && ` · ${i.proveedor}`}
                      {i.costoUnitario > 0 && ` · ${money(i.costoUnitario)} c/u`}
                    </p>
                  </div>
                  <button
                    onClick={() => setAjustando(i)}
                    className="shrink-0 rounded-xl px-2.5 py-1.5 text-right transition-colors hover:superficie-hundida"
                    aria-label={`Ajustar el stock de ${i.nombre}`}
                  >
                    <span
                      className={cn(
                        'cifra block font-display text-xl font-bold',
                        bajo ? 'text-ambar-600 dark:text-ambar-300' : 'texto-base',
                      )}
                    >
                      {num(i.stock)}
                    </span>
                    <span className="block text-[11px] texto-tenue">{i.unidad}</span>
                  </button>
                </div>

                {bajo && (
                  <p className="mt-2.5 rounded-xl bg-ambar-50 px-3 py-2 text-[12px] font-semibold text-ambar-700 dark:bg-ambar-500/10 dark:text-ambar-300">
                    Quedan {num(i.stock)} y el mínimo es {num(i.stockMinimo)}. Hay que reponer.
                  </p>
                )}

                {esAdmin && (
                  <div className="mt-3 flex gap-2 border-t borde-suave pt-3">
                    <Boton
                      tamano="sm"
                      variante="secundario"
                      icono={<Pencil className="size-3.5" />}
                      onClick={() => setEditando(i)}
                    >
                      Editar
                    </Boton>
                    <Boton tamano="sm" variante="secundario" onClick={() => setAjustando(i)}>
                      Ajustar stock
                    </Boton>
                  </div>
                )}
              </Tarjeta>
            )
          })}
        </div>
      )}

      {esAdmin && lista.length > 0 && (
        <div className="mt-6 border-t borde-suave pt-5">
          <Boton
            icono={<Plus className="size-[18px] " />}
            onClick={() => setEditando('nuevo')}
            className="w-full"
          >
            Nuevo insumo
          </Boton>
        </div>
      )}

      <FormularioInsumo
        insumo={editando}
        onCerrar={() => setEditando(null)}
        onGuardado={() => {
          setEditando(null)
          void cargar()
        }}
      />

      <AjustarStock
        insumo={ajustando}
        onCerrar={() => setAjustando(null)}
        onGuardado={(id, stock) => {
          setAjustando(null)
          setInsumos((s) => s.map((i) => (i.id === id ? { ...i, stock } : i)))
          avisar('Stock actualizado')
        }}
      />
    </>
  )
}

/* --------------------------- Ficha --------------------------- */

function FormularioInsumo({
  insumo,
  onCerrar,
  onGuardado,
}: {
  insumo: Insumo | 'nuevo' | null
  onCerrar: () => void
  onGuardado: () => void
}) {
  const avisar = useUI((s) => s.avisar)
  const editando = insumo && insumo !== 'nuevo' ? insumo : null

  const [codigo, setCodigo] = useState('')
  const [nombre, setNombre] = useState('')
  const [unidad, setUnidad] = useState('unidad')
  const [stockMinimo, setStockMinimo] = useState('0')
  const [costoUnitario, setCostoUnitario] = useState('0')
  const [proveedor, setProveedor] = useState('')
  const [notas, setNotas] = useState('')
  const [foto, setFoto] = useState<string | undefined>(undefined)
  const [activo, setActivo] = useState(true)
  const [guardando, setGuardando] = useState(false)
  const [borrando, setBorrando] = useState(false)
  const fotoRef = useRef<HTMLInputElement>(null)

  // Se rellena cada vez que se abre: la hoja no se desmonta entre un
  // insumo y otro, así que sin esto quedarían los datos del anterior.
  useEffect(() => {
    if (!insumo) return
    setCodigo(editando?.codigo ?? '')
    setNombre(editando?.nombre ?? '')
    setUnidad(editando?.unidad ?? 'unidad')
    setStockMinimo(String(editando?.stockMinimo ?? 0))
    setCostoUnitario(String(editando?.costoUnitario ?? 0))
    setProveedor(editando?.proveedor ?? '')
    setNotas(editando?.notas ?? '')
    setFoto(editando?.foto ?? undefined)
    setActivo(editando?.activo ?? true)
  }, [insumo, editando])

  const subirFoto = async (archivo?: File) => {
    if (!archivo) return
    try {
      setFoto(await comprimirImagen(archivo))
    } catch {
      avisar('No se pudo procesar la imagen.', 'error')
    }
  }

  const confirmar = async () => {
    if (!nombre.trim()) {
      avisar('Ponle un nombre al insumo.', 'error')
      return
    }
    if (!codigo.trim()) {
      avisar('Ponle un código: es lo que se escribe al recibir mercadería.', 'error')
      return
    }
    setGuardando(true)
    try {
      await guardarInsumo({
        id: editando?.id ?? uid('ins'),
        codigo: codigo.trim().toUpperCase(),
        nombre: nombre.trim(),
        unidad,
        stockMinimo: Number(stockMinimo) || 0,
        costoUnitario: Number(costoUnitario) || 0,
        proveedor: proveedor.trim() || null,
        notas: notas.trim() || null,
        foto: foto || null,
        activo,
      })
      avisar(editando ? 'Insumo actualizado' : 'Insumo creado')
      onGuardado()
    } catch (e) {
      avisar(e instanceof Error ? e.message : 'No se pudo guardar.', 'error')
    } finally {
      setGuardando(false)
    }
  }

  const borrar = async () => {
    if (!editando) return
    setBorrando(true)
    try {
      await eliminarInsumo(editando.id)
      avisar('Insumo eliminado')
      onGuardado()
    } catch {
      // Un insumo que ya viajó en un traspaso no se puede borrar: la
      // llave foránea lo impide, y está bien que así sea —borrarlo
      // dejaría envíos apuntando a la nada. Se ofrece darlo de baja.
      avisar('Este insumo ya salió en un envío, así que no se puede borrar. Dalo de baja.', 'error')
    } finally {
      setBorrando(false)
    }
  }

  return (
    <Hoja
      abierta={!!insumo}
      onCerrar={onCerrar}
      titulo={editando ? 'Editar insumo' : 'Nuevo insumo'}
      descripcion="Sale de la bodega central, pero no se le vende a nadie."
      pie={
        <div className="flex gap-2">
          {editando && (
            <Boton variante="peligro" cargando={borrando} onClick={borrar}>
              <Trash2 className="size-4" />
            </Boton>
          )}
          <Boton bloque cargando={guardando} onClick={confirmar}>
            {editando ? 'Guardar cambios' : 'Crear insumo'}
          </Boton>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Foto */}
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => fotoRef.current?.click()}
            className="grid size-20 shrink-0 place-items-center overflow-hidden rounded-2xl border-2 border-dashed borde-base texto-tenue transition-colors hover:border-bosque-400 hover:text-bosque-500"
          >
            {foto ? (
              <img src={foto} alt="" className="size-full object-cover" />
            ) : (
              <Camera className="size-6" />
            )}
          </button>
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-semibold texto-base">Foto del insumo</p>
            <p className="mt-0.5 text-[12px] texto-suave">
              Ayuda a reconocerlo rápido en la lista. Opcional.
            </p>
            {foto && (
              <button
                type="button"
                onClick={() => setFoto(undefined)}
                className="mt-1.5 inline-flex items-center gap-1 text-[12px] font-semibold text-rojo-500 hover:underline"
              >
                <Trash2 className="size-3.5" /> Quitar foto
              </button>
            )}
          </div>
          <input
            ref={fotoRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => void subirFoto(e.target.files?.[0])}
          />
        </div>

        <Campo etiqueta="Nombre" requerido>
          <Entrada
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Vaso de helado 250 cc"
          />
        </Campo>

        <div className="grid grid-cols-2 gap-3">
          <Campo etiqueta="Código" requerido ayuda="Identifica este insumo y no se repite.">
            <Entrada
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              placeholder="VAS-250"
              className="codigo"
            />
          </Campo>
          <Campo etiqueta="Se cuenta en">
            <Lista value={unidad} onChange={(e) => setUnidad(e.target.value)}>
              {UNIDADES_INSUMO.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </Lista>
          </Campo>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Campo etiqueta="Costo por unidad">
            <Entrada
              type="number"
              min={0}
              value={costoUnitario}
              onChange={(e) => setCostoUnitario(e.target.value)}
            />
          </Campo>
          <Campo etiqueta="Avisar bajo" ayuda="Cuando quede esto o menos.">
            <Entrada
              type="number"
              min={0}
              value={stockMinimo}
              onChange={(e) => setStockMinimo(e.target.value)}
            />
          </Campo>
        </div>

        <Campo etiqueta="Proveedor">
          <Entrada
            value={proveedor}
            onChange={(e) => setProveedor(e.target.value)}
            placeholder="Opcional"
          />
        </Campo>

        <Campo etiqueta="Notas">
          <Entrada
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            placeholder="Opcional. Ej: vienen en cajas de 500."
          />
        </Campo>

        {editando && (
          <Campo
            etiqueta="Estado"
            ayuda="Dado de baja deja de aparecer al armar un envío, pero conserva su historial."
          >
            <Lista value={activo ? 'si' : 'no'} onChange={(e) => setActivo(e.target.value === 'si')}>
              <option value="si">En uso</option>
              <option value="no">Dado de baja</option>
            </Lista>
          </Campo>
        )}

        {!editando && (
          <p className="rounded-2xl superficie-hundida px-4 py-3 text-[13px] texto-suave">
            El insumo se crea con stock en cero. Para cargarlo, usa <b>Ajustar stock</b> desde la
            lista.
          </p>
        )}
      </div>
    </Hoja>
  )
}

/* --------------------------- Ajustar stock --------------------------- */

function AjustarStock({
  insumo,
  onCerrar,
  onGuardado,
}: {
  insumo: Insumo | null
  onCerrar: () => void
  onGuardado: (id: string, stock: number) => void
}) {
  const avisar = useUI((s) => s.avisar)
  const [modo, setModo] = useState<'entrada' | 'conteo'>('entrada')
  const [cantidad, setCantidad] = useState('')
  const [observaciones, setObservaciones] = useState('')
  const [guardando, setGuardando] = useState(false)

  useEffect(() => {
    if (!insumo) return
    setModo('entrada')
    setCantidad('')
    setObservaciones('')
  }, [insumo])

  const confirmar = async () => {
    if (!insumo) return
    const valor = Number(cantidad)
    if (!cantidad.trim() || Number.isNaN(valor)) {
      avisar('Escribe una cantidad.', 'error')
      return
    }
    setGuardando(true)
    try {
      const autor = usuarioActual()
      // El backend hace la aritmética sobre lo que hay guardado en ese
      // momento, no sobre la copia que tiene esta pantalla.
      const stock = await moverStock([
        {
          id: uid('movins'),
          insumoId: insumo.id,
          tipo: modo === 'conteo' ? 'ajuste' : 'entrada',
          ...(modo === 'conteo' ? { stockFinal: valor } : { cantidad: valor }),
          usuarioId: autor.id,
          usuarioNombre: autor.nombre,
          observaciones: observaciones.trim() || undefined,
        },
      ])
      onGuardado(insumo.id, stock[insumo.id] ?? insumo.stock)
    } catch (e) {
      avisar(e instanceof Error ? e.message : 'No se pudo ajustar el stock.', 'error')
    } finally {
      setGuardando(false)
    }
  }

  return (
    <Hoja
      abierta={!!insumo}
      onCerrar={onCerrar}
      titulo={insumo?.nombre ?? ''}
      descripcion={insumo ? `Hoy hay ${num(insumo.stock)} ${insumo.unidad} en bodega.` : undefined}
      ancho="sm"
      pie={
        <Boton bloque cargando={guardando} onClick={confirmar}>
          {modo === 'conteo' ? 'Dejar en esa cantidad' : 'Sumar a la bodega'}
        </Boton>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-2">
          {(
            [
              ['entrada', 'Llegó mercadería'],
              ['conteo', 'Conteo físico'],
            ] as const
          ).map(([valor, etiqueta]) => (
            <button
              key={valor}
              onClick={() => setModo(valor)}
              className={cn(
                'rounded-xl border px-3 py-2.5 text-[13px] font-semibold transition-colors',
                modo === valor
                  ? 'border-bosque-500 bg-bosque-50 text-bosque-600 dark:bg-bosque-500/15 dark:text-bosque-200'
                  : 'borde-base texto-suave hover:superficie-hundida',
              )}
            >
              {etiqueta}
            </button>
          ))}
        </div>

        <Campo
          etiqueta={modo === 'conteo' ? 'Cuánto hay de verdad' : 'Cuánto llegó'}
          ayuda={
            modo === 'conteo'
              ? 'El stock queda exactamente en este número. La diferencia se anota sola.'
              : 'Se suma a lo que ya había. Para descontar, escribe un número negativo.'
          }
          requerido
        >
          <Entrada
            type="number"
            inputMode="decimal"
            value={cantidad}
            onChange={(e) => setCantidad(e.target.value)}
            placeholder={modo === 'conteo' ? String(insumo?.stock ?? 0) : '100'}
          />
        </Campo>

        <Campo etiqueta="Motivo" ayuda="Opcional, pero ayuda a entender el movimiento después.">
          <Entrada
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            placeholder={modo === 'conteo' ? 'Conteo de fin de mes' : 'Compra a proveedor'}
          />
        </Campo>
      </div>
    </Hoja>
  )
}
